import { useEffect } from 'react';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Select, MenuItem, FormControl, FormHelperText, IconButton, TextField } from '@mui/material';
import { SCUIButton } from '../../../../../../atomic/atoms';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { handleStudioApiResponse } from '../../../../../helper/studio_helper';
import { PlanningCommand } from '../../../../../../services/studio/StudioCommand';

export const AssigneeTab = ({
    studios,
    employees,
    employeeTitles,
    setIsLoading,
    planningDetail,
    setActiveStep,
    setPlanningDetail,
}: any) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<any>({
        defaultValues: {
            planingDate: new Date().toISOString().split('T')[0],
            studioId: '',
            planingEmployees: [{ employeeTitleId: '', employeeId: '' }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'planingEmployees',
    });

    const onSubmit = (data: any) => {
        setIsLoading(true);
        if (!planningDetail) {
            handleStudioApiResponse(
                PlanningCommand.PostPlanning({
                    planingDate: data.planingDate,
                    studioId: data.studioId,
                    employeeIds: data.planingEmployees?.map((item: any) => item.employeeId),
                }),
                (res: any) => {
                    setPlanningDetail({ ...data, id: res.model?.id });
                    setActiveStep(2);
                },
                () => setIsLoading(false),
                t
            );
            return;
        }
        handleStudioApiResponse(
            PlanningCommand.PutPlanning({
                planingDate: data.planingDate,
                studioId: data.studioId,
                employeeIds: data.planingEmployees?.map((item: any) => item.employeeId),
                id: planningDetail.id,
            }),
            () => {
                setPlanningDetail({ ...data, id: planningDetail?.id });
                setActiveStep(2);
            },
            () => setIsLoading(false),
            t
        );
    };

    useEffect(() => {
        if (planningDetail) {
            reset({
                planingDate: planningDetail.planingDate?.split('T')[0],
                studioId: planningDetail.studioId,
                planingEmployees: planningDetail.planingEmployees?.map((pe: any) => ({
                    employeeId: pe.employeeId,
                    employeeTitleId: employees.find((e: any) => e.id === pe.employeeId)?.title ?? '',
                })),
            });
        }
    }, [planningDetail, employees]);

    return (
        <Box className="studio-form-paper row centered" sx={{ minWidth: '50%', marginTop: '24px' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl fullWidth>
                        <p className="studio-form-label">{t('general.date')}</p>
                        <Controller
                            name="date"
                            control={control}
                            defaultValue={new Date().toISOString().split('T')[0]}
                            rules={{ required: t('studio.planning.required') }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    type="date"
                                    size="small"
                                    fullWidth
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true,
                                        },
                                    }}
                                    error={!!errors.planingDate}
                                    helperText={errors.planingDate?.message?.toString()}
                                />
                            )}
                        />
                    </FormControl>
                    <FormControl fullWidth error={!!errors.studioId}>
                        <p className="studio-form-label">{t('studio.studioName')}</p>
                        <Controller
                            name="studioId"
                            control={control}
                            rules={{ required: t('studio.planning.required') }}
                            render={({ field }) => (
                                <Select {...field} size="small" displayEmpty error={!!errors.studioId}>
                                    <MenuItem value="">
                                        <em>{t('general.select')}</em>
                                    </MenuItem>
                                    {studios.map((studio: any) => (
                                        <MenuItem key={studio.id} value={studio.id}>
                                            {studio.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        <FormHelperText>{errors.studioId?.message?.toString()}</FormHelperText>
                    </FormControl>
                    <Box>
                        {fields.map((item, index) => {
                            const selectedTitleId = watch(`planingEmployees.${index}.employeeTitleId`);
                            const filteredEmployees = employees.filter((emp: any) => emp.title === selectedTitleId);

                            return (
                                <Box key={item.id} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                    <Box>
                                        {index === 0 && (
                                            <p className="studio-form-label">
                                                {t('studio.planning.employeeAssignments')}
                                            </p>
                                        )}
                                        <Controller
                                            name={`planingEmployees.${index}.employeeTitleId`}
                                            control={control}
                                            render={({ field }) => (
                                                <FormControl sx={{ minWidth: 200 }} size="small">
                                                    <Select {...field} displayEmpty>
                                                        <MenuItem value="">
                                                            <em>{t('general.select')}</em>
                                                        </MenuItem>
                                                        {employeeTitles.map((title: any) => (
                                                            <MenuItem key={title.id} value={title.id}>
                                                                {title.title}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Box>
                                    <Box>
                                        {index === 0 && (
                                            <p className="studio-form-label">{t('studio.planning.employeeNames')}</p>
                                        )}
                                        <Controller
                                            name={`planingEmployees.${index}.employeeId`}
                                            control={control}
                                            render={({ field }) => (
                                                <FormControl sx={{ minWidth: 200 }} size="small">
                                                    <Select {...field} displayEmpty>
                                                        <MenuItem value="">
                                                            <em>{t('general.select')}</em>
                                                        </MenuItem>
                                                        {filteredEmployees.map((emp: any) => (
                                                            <MenuItem key={emp.id} value={emp.id}>
                                                                {emp.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Box>
                                    {index !== 0 && (
                                        <IconButton
                                            sx={{ cursor: 'pointer' }}
                                            color="error"
                                            onClick={() => remove(index)}
                                        >
                                            <RemoveCircleIcon sx={{ fontSize: 32 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <IconButton
                                sx={{ cursor: 'pointer' }}
                                onClick={() => append({ employeeTitleId: '', employeeId: '' })}
                                color="primary"
                            >
                                <AddCircleIcon sx={{ fontSize: 32 }} />
                            </IconButton>
                            <p
                                onClick={() => append({ employeeTitleId: '', employeeId: '' })}
                                className="studio-form-label"
                                style={{ marginLeft: '8px', cursor: 'pointer' }}
                            >
                                {t('studio.planning.addEmployee')}
                            </p>
                        </Box>
                    </Box>
                    <SCUIButton
                        text={t('general.continue')}
                        style={{ height: '35px' }}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        onClick={handleSubmit(onSubmit)}
                    />
                </Box>
            </form>
        </Box>
    );
};
